import { useState, useEffect, useMemo } from 'react'
import { getMyImmobilierAnnonces } from '../../services/api.js'

function AnnonceRow({ annonce }) {
  const title = annonce.titre ?? annonce.title ?? '—'
  const city = annonce.ville ?? annonce.city ?? annonce.localisation ?? '—'
  const price = annonce.prix ?? annonce.price
  const surface = annonce.surface
  const rooms = annonce.nb_pieces ?? annonce.rooms
  const txType = annonce.type_transaction ?? annonce.transactionType ?? ''
  const type = annonce.type_bien ?? annonce.propertyType ?? ''
  const url = annonce.url ?? annonce.sourceUrl
  const agency = annonce.agence ?? annonce.agencyName
  const phone = annonce.tel_agence ?? annonce.agencyPhone

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 12,
      padding: '14px 16px',
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 10,
      transition: 'border-color 0.15s',
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(91, 92, 244,0.25)'}
    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          {txType && (
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4,
              background: txType === 'vente' ? 'rgba(0,200,100,0.15)' : 'rgba(0,180,80,0.15)',
              color: txType === 'vente' ? 'rgba(0,220,100,0.9)' : 'rgba(0,160,255,0.9)',
            }}>
              {txType}
            </span>
          )}
          {type && (
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>{type}</span>
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'rgba(255,255,255,0.45)', flexWrap: 'wrap' }}>
          <span>📍 {city}</span>
          {surface && <span>{surface} m²</span>}
          {rooms && <span>{rooms} pièces</span>}
          {agency && <span style={{ color: 'rgba(255,255,255,0.25)' }}>{agency}</span>}
          {phone && <span style={{ color: 'rgba(255,255,255,0.25)' }}>{phone}</span>}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
        {price ? (
          <span style={{ fontSize: 15, fontWeight: 700, color: '#5b5cf4' }}>
            {Number(price).toLocaleString('fr-FR')} €
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>Prix n/d</span>
        )}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, color: 'rgba(91, 92, 244,0.7)', textDecoration: 'none', whiteSpace: 'nowrap' }}
            onMouseEnter={e => e.target.style.color = '#5b5cf4'}
            onMouseLeave={e => e.target.style.color = 'rgba(91, 92, 244,0.7)'}
          >
            Voir l'annonce →
          </a>
        )}
      </div>
    </div>
  )
}

const PAGE_SIZE = 10

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 7,
  padding: '6px 10px',
  color: '#fff',
  fontSize: 12,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
}

const selectStyle = { ...inputStyle, background: '#1a1a2e' }

const EMPTY_FILTERS = { search: '', txType: '', propertyType: '', priceMax: '', surfaceMin: '', dateFrom: '', dateTo: '' }

/** @param {import('react').ReactNode} emptyState  affiché quand il n'y a aucune annonce (sinon rien). */
export default function ImmobilierMyAnnonces({ refreshKey, emptyState = null }) {
  const [annonces, setAnnonces] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    setLoading(true)
    getMyImmobilierAnnonces()
      .then(data => { setAnnonces(data.annonces ?? []); setPage(1) })
      .catch(() => setAnnonces([]))
      .finally(() => setLoading(false))
  }, [refreshKey])

  const handleFilter = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setPage(1)
  }

  const resetFilters = () => { setFilters(EMPTY_FILTERS); setPage(1) }

  const hasActiveFilters = Object.values(filters).some(v => v !== '')

  const filtered = useMemo(() => {
    const parsePrice = (val) => parseFloat(String(val ?? '').replace(/[^\d.]/g, '')) || 0
    const parseSurface = (val) => parseFloat(String(val ?? '').replace(/[^\d.]/g, '')) || 0

    return annonces.filter(a => {
      const city = (a.ville ?? a.city ?? a.localisation ?? '').toLowerCase()
      const title = (a.titre ?? a.title ?? '').toLowerCase()
      const txType = (a.type_transaction ?? a.transactionType ?? '').toLowerCase()
      const type = (a.type_bien ?? a.propertyType ?? '').toLowerCase()
      const price = parsePrice(a.prix ?? a.price)
      const surface = parseSurface(a.surface)

      if (filters.search && !city.includes(filters.search.toLowerCase()) && !title.includes(filters.search.toLowerCase())) return false
      if (filters.txType && txType !== filters.txType.toLowerCase()) return false
      if (filters.propertyType && type !== filters.propertyType.toLowerCase()) return false
      if (filters.priceMax && price > 0 && price > Number(filters.priceMax)) return false
      if (filters.surfaceMin && surface > 0 && surface < Number(filters.surfaceMin)) return false
      const dateRaw = a.date_publication ?? a.publishedAt ?? a.date_scraping
      if (filters.dateFrom && dateRaw && new Date(dateRaw) < new Date(filters.dateFrom)) return false
      if (filters.dateTo && dateRaw && new Date(dateRaw) > new Date(filters.dateTo + 'T23:59:59')) return false
      return true
    })
  }, [annonces, filters])

  if (loading) return null
  if (annonces.length === 0) return emptyState

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div style={{ marginTop: 40 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'rgba(255,255,255,0.5)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Mes annonces acquises
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasActiveFilters && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              {filtered.length} / {annonces.length}
            </span>
          )}
          <span style={{ fontSize: 11, color: 'rgba(91, 92, 244,0.6)', background: 'rgba(91, 92, 244,0.08)', padding: '3px 10px', borderRadius: 12 }}>
            {annonces.length} annonce{annonces.length > 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setShowFilters(v => !v)}
            style={{
              background: showFilters ? 'rgba(91, 92, 244,0.12)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${showFilters ? 'rgba(91, 92, 244,0.35)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 7,
              color: showFilters ? 'rgba(91, 92, 244,0.9)' : 'rgba(255,255,255,0.45)',
              fontSize: 12,
              padding: '4px 12px',
              cursor: 'pointer',
            }}
          >
            {showFilters ? '▲ Filtres' : '▼ Filtres'}{hasActiveFilters ? ' •' : ''}
          </button>
        </div>
      </div>

      {/* Panneau de filtres */}
      {showFilters && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 14, padding: '14px', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Ville / titre</label>
            <input name="search" value={filters.search} onChange={handleFilter} placeholder="Paris, appartement..." style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Transaction</label>
            <select name="txType" value={filters.txType} onChange={handleFilter} style={selectStyle}>
              <option value="">Toutes</option>
              <option value="vente">Vente</option>
              <option value="location">Location</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Type de bien</label>
            <select name="propertyType" value={filters.propertyType} onChange={handleFilter} style={selectStyle}>
              <option value="">Tous</option>
              <option value="appartement">Appartement</option>
              <option value="maison">Maison / Villa</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Prix max (€)</label>
            <input name="priceMax" type="number" min="0" value={filters.priceMax} onChange={handleFilter} placeholder="500000" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Surface min (m²)</label>
            <input name="surfaceMin" type="number" min="0" value={filters.surfaceMin} onChange={handleFilter} placeholder="40" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Publiée depuis</label>
            <input name="dateFrom" type="date" value={filters.dateFrom} onChange={handleFilter} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Publiée jusqu'au</label>
            <input name="dateTo" type="date" value={filters.dateTo} onChange={handleFilter} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              style={{ padding: '6px 12px', borderRadius: 7, background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: hasActiveFilters ? 'rgba(255,100,100,0.7)' : 'rgba(255,255,255,0.2)', cursor: hasActiveFilters ? 'pointer' : 'default', fontSize: 12, whiteSpace: 'nowrap' }}
            >
              Réinitialiser
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      {filtered.length === 0 ? (
        <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13, borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          Aucune annonce ne correspond aux filtres.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {paginated.map((a, i) => (
            <AnnonceRow key={a.id ?? i} annonce={a} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: page === 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}
          >
            ←
          </button>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ padding: '5px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: page === totalPages ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)', cursor: page === totalPages ? 'default' : 'pointer', fontSize: 13 }}
          >
            →
          </button>
        </div>
      )}
    </div>
  )
}
